<template>
  <form class="feedback-form" @submit.prevent="handleSubmit">
    <div class="form-group">
      <label class="form-label">{{ $t('feedback.name') }}</label>
      <input
        v-model="form.name"
        type="text"
        class="iso-input"
        :placeholder="$t('feedback.namePlaceholder')"
        required
      />
    </div>
    <div class="form-group">
      <label class="form-label">{{ $t('feedback.email') }}</label>
      <input
        v-model="form.email"
        type="email"
        class="iso-input"
        :placeholder="$t('feedback.emailPlaceholder')"
        required
      />
    </div>
    <div class="form-group">
      <label class="form-label">{{ $t('feedback.content') }}</label>
      <textarea
        v-model="form.content"
        class="iso-input form-textarea"
        :placeholder="$t('feedback.contentPlaceholder')"
        rows="5"
        required
      ></textarea>
    </div>
    <div class="form-actions">
      <button type="submit" class="iso-btn" :disabled="submitting">
        {{ submitting ? $t('common.loading') : $t('feedback.submit') }}
      </button>
    </div>
    <transition name="msg-fade">
      <p v-if="message" class="form-message" :class="messageType">{{ message }}</p>
    </transition>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { request } from '@/api'
import { debounce } from '@/utils'

const { t } = useI18n()

const form = reactive({
  name: '',
  email: '',
  content: '',
})

const submitting = ref(false)
const message = ref('')
const messageType = ref<'success' | 'error'>('success')

const debouncedSubmit = debounce(async () => {
  try {
    await request('/feedback', {
      method: 'POST',
      body: JSON.stringify(form),
    })
    message.value = t('feedback.success')
    messageType.value = 'success'
    form.name = ''
    form.email = ''
    form.content = ''
  } catch {
    message.value = t('feedback.fail')
    messageType.value = 'error'
  } finally {
    submitting.value = false
  }
}, 500)

async function handleSubmit() {
  if (submitting.value) return
  submitting.value = true
  message.value = ''
  debouncedSubmit()
}
</script>

<style scoped>
.feedback-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--iso-text-dim);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.form-message {
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
}

.form-message.success {
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: var(--iso-cyan);
}

.form-message.error {
  background: rgba(255, 80, 80, 0.1);
  border: 1px solid rgba(255, 80, 80, 0.2);
  color: #ff5050;
}

.msg-fade-enter-active,
.msg-fade-leave-active {
  transition: opacity 0.3s;
}

.msg-fade-enter-from,
.msg-fade-leave-to {
  opacity: 0;
}
</style>
